import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import __ from "lodash";
import {FormControl, FormGroup, Grid} from "@material-ui/core";
import { v4 as uuidv4 } from 'uuid';

import {DatePicker, FormDialog, FormSelect, LabelledPaper, MaskedNumber, MaskedText} from "../../../controls";
import PassportEditor from "./PassportEditor";
import Validator from "../validators/EditPersonValidator";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";


const paperProps = {
  style: {
    backgroundColor: "#f8f8f8",
    boxShadow: 'none',
  }
}

const defaultLabelledPaperProps = {
  checkChildren: true,
  style: {display: "block", border: "solid 1px #b9b9b9" },
  labelStyle: {background: "#f0f0f0"},
  collapsible: false,
}

const salutations = [
  {"code":"MR","value":"Mr"},
  {"code":"MRS","value":"Mrs"},
  {"code":"Ms","value":"Ms"},
];

const sex = [
  {"code":"MALE","value":"Male"},
  {"code":"FEMALE","value":"Female"},
];

const dateFormat = "DD/MM/YYYY";

const EditPersonDialog = (SystemData, apiSitePrefix, extraKeys, handleAdd, handleUpdate, formatChars, classes, updateParent = null) => {
  const requestRef = createRef(0);
  const eventBus = SystemData.eventBus;

  const [dialogStates, setDialogStates] = useState({
    showDialog: false,
    record: {},
    title: 'Person Details',
    extraKeys: extraKeys,
    newPerson: false
  });

  const [editing, setEditing] = useState(false);

  const queryClient = useQueryClient();

  const open = async (record, updateKeys, newPerson) => {
    setDialogStates({...dialogStates, showDialog:true, record, extraKeys: updateKeys||extraKeys, newPerson: newPerson || false, });
  }
  const close = () => {
    setDialogStates({...dialogStates, showDialog:false, record: {}});
  }

  const handleAddPassport = () => {
    if (eventBus) {
      eventBus.trigger('Person.AddPassport');
    }
  }

  const handlePassportChanged = (changes, props) => {
    const {record, recordChange} = props;
    if (changes && changes.record) {
      const passports =  changes.record.passports;
      record['passports'] = passports;
      recordChange({...record, passports});

      if (changes.resolve) {
        changes.resolve();
      }
    }
  }

  const Dialog = (props) => {

    if (dialogStates.showDialog) {
      return (
        <FormDialog
          title={`${dialogStates.newPerson ? "New" : "Edit"} ${dialogStates.title}`}
          message=""
          open={dialogStates.showDialog}
          record={dialogStates.record}
          dialogOptions={{maxWidth: "md", fullWidth: true, PaperProps: paperProps }}
          classes={classes}
          validator={Validator}
          buttons = {
            (dialogStates.newPerson || editing)
              ?
                {
                  addPassport: {icon: "add_circle", key: "Add Passport", label: "Add Passport", color: "primary"},
                  save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return status.error}},
                  cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
                }
              : (!editing)
                ?
                  {
                    edit: {icon: "edit", key: "Edit", label: "Edit", color: "primary"},
                    cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
                  }
                :
                  {
                    cancel:{icon: "cancel", key: "Cancel", label: "Close", color: "primary"}
                  }
          }
          onChange={(state) => {
            if (state.buttonPressed === "save") {
              const record = state.record;
              if (dialogStates.newPerson) {
                record["personUuid"] = uuidv4();
                handleAdd.mutate({
                  extraKeys: dialogStates.extraKeys,
                  record,
                  postSuccess: () => {
                    setDialogStates({...dialogStates, showDialog: false, record: null});
                    if (props.afterSave) {
                      props.afterSave();
                    }
                    if (updateParent)
                      updateParent(record);
                  },
                  postError: (data) => {
                    if (props.onError) {
                      props.onError(data);
                    } else if (data.errorMessage) {
                      SystemData.alert("Error", `${data.errorMessage}`);
                    } else {
                      SystemData.alert("Error", `An Unexpected Error has occurred.`);
                    }
                  }
                });
              } else {
                handleUpdate.mutate({
                  extraKeys: dialogStates.extraKeys,
                  record,
                  postSuccess: () => {
                    setDialogStates({...dialogStates, showDialog: false, record: null});
                    if (props.afterSave) {
                      props.afterSave();
                    }
                    if (updateParent)
                      updateParent(record);
                  },
                  postError: (data) => {
                    if (props.onError) {
                      props.onError(data);
                    } else if (data.errorMessage) {
                      SystemData.alert("Error", `${data.errorMessage}`);
                    } else {
                      SystemData.alert("Error", `An Unexpected Error has occurred.`);
                    }
                  }
                });
              }
            } else if (state.buttonPressed === "edit") {
              setEditing(true);
            } else if (state.buttonPressed === "addPassport") {
              handleAddPassport();
            } else {
              setEditing(false);
              setDialogStates({...dialogStates, showDialog: false, record: null});
            }
          }}

        >
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={12} checkChildren={true} row>
              <LabelledPaper {...defaultLabelledPaperProps} label="Person" >
                <FormControl checkChildren={true} key="FC-person" style={{display: "block" }}>
                  <FormGroup checkChildren={true} key="FG-person" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={2} checkchildren="true">
                        <FormSelect
                          id="title"
                          label="Title"
                          options={salutations ? salutations : null}
                          readOnly={!dialogStates.newPerson && editing!==true}
                          style={{marginTop: "1px"}}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={3} checkChildren={true}>
                        <MaskedText
                          id="forename"
                          label="First Name"
                          inputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          mask={"a".repeat(30)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={{ a: "[a-zA-Z\- ]" }}
                          forceUpperCase={false}
                          readOnly={!dialogStates.newPerson && editing!==true}
                          onChange={(value, props) => {
                            props.onChange(__.capitalize(value));
                          }}
                        />
                      </Grid>
                      <Grid item xs={3} checkChildren={true}>
                        <MaskedText
                          id="middleName"
                          label="Middle Name"
                          inputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          mask={"a".repeat(30)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={{ a: "[a-zA-Z\- ]" }}
                          forceUpperCase={true}
                          readOnly={!dialogStates.newPerson && editing!==true}
                          onChange={(value, props) => {
                            props.onChange(__.capitalize(value));
                          }}
                        />
                      </Grid>
                      <Grid item xs={4} checkChildren={true}>
                        <MaskedText
                          id="surname"
                          label="Last Name"
                          inputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          mask={"a".repeat(30)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={{ a: "[a-zA-Z0-9 ]" }}
                          forceUpperCase={true}
                          readOnly={!dialogStates.newPerson && editing!==true}
                          onChange={(value, props) => {
                            props.onChange(__.capitalize(value));
                          }}
                        />
                      </Grid>
                      <Grid item xs={5} checkChildren={true}>
                        <MaskedText
                          id="knownAs"
                          label="Known As"
                          inputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          mask={"a".repeat(30)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={{ a: "[a-zA-Z\- ]" }}
                          forceUpperCase={true}
                          readOnly={!dialogStates.newPerson && editing!==true}
                          onChange={(value, props) => {
                            props.onChange(__.capitalize(value));
                          }}
                        />
                      </Grid>

                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            <Grid item xs={12} checkChildren={true} row>
              <LabelledPaper {...defaultLabelledPaperProps} label="Details" >
                <FormControl checkChildren={true} key="FC-persondetails" style={{display: "block" }}>
                  <FormGroup checkChildren={true} key="FG-persondetails" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={2} checkchildren="true">
                        <FormSelect
                          id="sex"
                          label="Sex"
                          options={sex ? sex : null}
                          readOnly={!dialogStates.newPerson && editing!==true}
                          style={{marginTop: "3px"}}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={3} checkchildren="true">
                        <DatePicker
                          id="doB"
                          label="Date of Birth"
                          disableToolbar={true}
                          autoOk={true}
                          format={dateFormat}
                          readOnly={!dialogStates.newPerson && editing!==true}
                        />
                      </Grid>
                      <Grid item xs={3} checkChildren={true}>
                        <MaskedNumber
                          id="phone"
                          label="Phone Number"
                          readOnly={!dialogStates.newPerson && editing!==true}
                          inputLabelProps={{shrink: true}}
                          fullWidth
                          format="+##############"
                        />
                      </Grid>
                      <Grid item xs={4} checkChildren={true}>
                        <MaskedText
                          id="email"
                          label="Email"
                          inputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          mask={"a".repeat(30)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={{ a: "[a-zA-Z0-9\@\.\-]" }}
                          forceUpperCase={false}
                          readOnly={!dialogStates.newPerson && editing!==true}
                        />
                      </Grid>
                      <Grid item xs={5} checkChildren={true}>
                        <CompanyUuidLookup
                          id="companyUuid"
                          label="Company"
                          shrink
                          readOnly={!dialogStates.newPerson && editing!==true}
                          returnType={"object"}
                          includeNew={true}
                          onChange={(returnObject, props) => {
                            if (returnObject.found) {
                              const company = returnObject.data
                              const data = {
                                ...props.record,
                                companyUuid: company.companyUuid,
                                companyName: company.companyName
                              }
                              props.recordChange(data);
                            } else {
                              props.onChange(returnObject.value);
                            }
                          }}
                        />
                      </Grid>

                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            <Grid item xs={12} checkChildren={true}>
              <LabelledPaper {...defaultLabelledPaperProps} label="Passports" >
                <FormControl checkChildren={true} key="FC-passports" style={{display: "block" }}>
                  <FormGroup checkChildren={true} key="FG-passports" style={{padding: "8px 8px 8px 8px"}}>
                    {(record, changeRecord) => (
                      <PassportEditor
                        personRecord={record}
                        handleUpdate={handleUpdate}
                        extraKeys={extraKeys}
                        canEdit={dialogStates.newPerson || editing}
                        classes={classes}
                        onChange={handlePassportChanged}
                      />
                    )}
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>
          </Grid>
        </FormDialog>
      )
    }
    return (<></>);
  }
  return {
    Dialog,
    open,
    close
  }

}

export default EditPersonDialog;
